import React, { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, Plus, Search, X, Pencil } from "lucide-react";

/**
 * Searchable dropdown with "Add new" support.
 * options: [{ value, label, subtype?, meta? }]
 * onEditOption(option): if provided, shows a pencil icon on each option row
 */
export default function Combobox({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  onAddNew,
  onEditOption,
  testid,
  disabled,
  allowClear = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const filtered = options.filter((o) =>
    (o.label || "").toLowerCase().includes(query.toLowerCase())
  );

  const selectedLabel = options.find((o) => o.value === value)?.label || value || "";

  const handleAdd = async () => {
    if (!newName.trim() || !onAddNew) return;
    const created = await onAddNew(newName.trim());
    if (created) {
      onChange(created.value || created.name);
      setAdding(false);
      setNewName("");
      setQuery("");
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        data-testid={testid}
        className={`w-full h-[52px] px-4 pr-10 border border-slate-300 rounded-xl bg-white text-left text-base transition-colors hover:border-slate-400 ${
          disabled ? "opacity-60 cursor-not-allowed" : ""
        } ${open ? "border-[#0055FF] ring-4 ring-[#0055FF]/20" : ""}`}
      >
        <span className={selectedLabel ? "text-slate-900" : "text-slate-400"}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        {allowClear && selectedLabel && !disabled && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="absolute right-9 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          >
            <X size={14} />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <Search size={16} className="text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full text-sm outline-none bg-transparent"
              data-testid={testid ? `${testid}-search` : undefined}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="px-4 py-3 text-sm text-slate-400">No results</div>
            )}
            {filtered.map((o) => (
              <div
                key={o.value}
                className="w-full text-left flex items-center justify-between hover:bg-slate-50"
              >
                <button
                  type="button"
                  onClick={() => { onChange(o.value); setOpen(false); setQuery(""); }}
                  className="flex-1 text-left px-4 py-2.5 text-sm flex items-center justify-between"
                  data-testid={testid ? `${testid}-option-${o.value}` : undefined}
                >
                  <span className="text-slate-800">{o.label}</span>
                  {o.value === value && <Check size={16} className="text-[#0055FF]" />}
                </button>
                {onEditOption && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onEditOption(o); }}
                    className="px-3 text-slate-400 hover:text-[#0055FF]"
                    title="Rename"
                    data-testid={testid ? `${testid}-edit-${o.value}` : undefined}
                  >
                    <Pencil size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {onAddNew && (
            <div className="border-t border-slate-100 p-2">
              {!adding ? (
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#0055FF] hover:bg-blue-50 rounded-lg"
                  data-testid={testid ? `${testid}-add-new` : undefined}
                >
                  <Plus size={16} /> Add new
                </button>
              ) : (
                <div className="flex gap-2">
                  <input
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                    placeholder="New name"
                    className="flex-1 px-3 h-10 border border-slate-300 rounded-lg text-sm outline-none"
                    data-testid={testid ? `${testid}-new-name` : undefined}
                  />
                  <button
                    type="button"
                    onClick={handleAdd}
                    className="px-3 h-10 rounded-lg bg-[#0055FF] text-white text-sm font-semibold"
                    data-testid={testid ? `${testid}-save-new` : undefined}
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
